import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toPng, toSvg } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FlaskConical, Download, FileText, Loader2, ArrowLeft, Image } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';
import { Footer } from '@/components/Footer';
import { ThemeToggle } from '@/components/ThemeToggle';
import PageSEO from '@/components/PageSEO';

const FORMATS = {
  '16:9':   { w: 1920, h: 1080, label: '16:9 Presentation' },
  '4:3':    { w: 1600, h: 1200, label: '4:3 Standard' },
  'social': { w: 1200, h: 630,  label: 'Social Media (OG)' },
};

/* ─── helper: truncate ───────────────────────────────────────────────── */
const tr = (s, n) => (s && s.length > n ? s.slice(0, n) + '...' : s || '');

/* ─── PICO row ───────────────────────────────────────────────────────── */
const PicoRow = ({ label, value, color, fs = 12 }) => value ? (
  <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
    <span style={{ fontWeight: 700, fontSize: fs - 1, color: '#fff', background: color, borderRadius: 3, padding: '1px 6px', minWidth: 22, textAlign: 'center', flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: fs, lineHeight: 1.35, color: '#334155' }}>{tr(value, 250)}</span>
  </div>
) : null;

/* ─── Score circle ───────────────────────────────────────────────────── */
const ScoreCircle = ({ value, label, color, max, size = 90, fontSize = 24 }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{
      width: size, height: size, borderRadius: '50%', border: `${size > 60 ? 4 : 3}px solid ${color}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: '#fff', boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
    }}>
      <span style={{ fontSize, fontWeight: 800, color, lineHeight: 1 }}>{value}</span>
      <span style={{ fontSize: Math.max(7, fontSize * 0.35), color: '#94a3b8', marginTop: 1 }}>/ {max}</span>
    </div>
    <div style={{ fontSize: Math.max(7, size * 0.1), fontWeight: 600, color: '#475569', marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</div>
  </div>
);

/* ─── Section box ────────────────────────────────────────────────────── */
const Box = ({ title, color = '#475569', children, style = {} }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', ...style }}>
    <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>{title}</div>
    {children}
  </div>
);

/* ═══════════════════════ SLIDE CANVAS ════════════════════════════════ */
const SlideCanvas = ({ data, ratio, canvasRef }) => {
  const fmt = FORMATS[ratio] || FORMATS['16:9'];
  const W = fmt.w;
  const H = fmt.h;
  const isSocial = ratio === 'social';
  const is43 = ratio === '4:3';

  const impact = data.impact_index != null ? data.impact_index.toFixed(1) : '—';
  const methScore = data.methodology_score != null ? data.methodology_score : '—';
  const visScore = data.visibility_score != null ? data.visibility_score : '—';
  const authors = (data.authors || []).slice(0, 8).join(', ') + (data.authors?.length > 8 ? ' et al.' : '');
  const pico = data.pico || {};
  const strengths = data.methodological_strengths || [];
  const limitations = data.methodological_limitations || [];
  const gaps = data.research_gaps || [];
  const takeaway = data.key_takeaway || '';
  const insight = data.key_research_insight || '';
  const overview = data.study_overview || '';
  const evidence = data.evidence_contribution || '';

  /* ── adaptive sizes ── */
  const pad = isSocial ? '12px 24px' : '24px 44px';
  const hPad = isSocial ? '14px 24px' : '26px 44px';
  const titleFs = isSocial ? 16 : (is43 ? 26 : 26);
  const bodyFs = isSocial ? 10 : 13;
  const sCircle = isSocial ? 62 : (is43 ? 90 : 90);
  const sFont = isSocial ? 20 : 26;
  const maxTakeaway = isSocial ? 380 : 800;
  const maxInsight = isSocial ? 320 : 700;
  const maxOverview = isSocial ? 350 : 800;
  const maxStrength = isSocial ? 120 : 250;
  const maxGap = isSocial ? 130 : 250;

  return (
    <div ref={canvasRef} style={{
      width: W, height: H, fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
      background: 'linear-gradient(135deg,#f8fafc 0%,#e2e8f0 100%)', overflow: 'hidden', color: '#1e293b',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* ── HEADER ── */}
      <div style={{
        background: 'linear-gradient(90deg,#1e3a5f 0%,#2563eb 100%)',
        padding: hPad, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isSocial ? 8 : 12 }}>
          <svg width={isSocial ? 20 : 30} height={isSocial ? 20 : 30} viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 2v7.527a2 2 0 0 1-.211.896L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.069-10.127A2 2 0 0 1 14 9.527V2" />
            <path d="M8.5 2h7" /><path d="M7 16h10" />
          </svg>
          <div>
            <span style={{ color: '#fff', fontSize: isSocial ? 14 : 20, fontWeight: 800 }}>Evidentica</span>
            {!isSocial && <div style={{ color: '#93c5fd', fontSize: 10, fontWeight: 500 }}>Research Visibility Platform</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {data.normalized_study_type && (
            <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: isSocial ? '2px 8px' : '4px 12px', borderRadius: 16, fontSize: isSocial ? 9 : 11, fontWeight: 600 }}>
              {data.normalized_study_type}
            </span>
          )}
          {data.evidence_level && (
            <span style={{ background: '#fbbf24', color: '#78350f', padding: isSocial ? '2px 8px' : '4px 12px', borderRadius: 16, fontSize: isSocial ? 9 : 11, fontWeight: 700 }}>
              {data.evidence_level}
            </span>
          )}
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ padding: pad, flex: 1, display: 'flex', flexDirection: 'column', gap: isSocial ? 8 : 14, overflow: 'hidden' }}>
        {/* Title row + scores */}
        <div style={{ display: 'flex', gap: isSocial ? 16 : 28, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: titleFs, fontWeight: 800, lineHeight: 1.2, color: '#0f172a', margin: 0, display: '-webkit-box', WebkitLineClamp: isSocial ? 2 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {data.title}
            </h1>
            <div style={{ fontSize: isSocial ? 9 : 11, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>
              {authors}
              {data.journal && <><br /><span style={{ fontWeight: 600, color: '#475569' }}>{data.journal}</span>{data.publication_year ? ` (${data.publication_year})` : ''}</>}
              {data.doi && <>{' '}<span style={{ color: '#94a3b8' }}>DOI: {data.doi}</span></>}
            </div>
          </div>
          {/* Scores */}
          <div style={{ display: 'flex', gap: isSocial ? 10 : 18, flexShrink: 0, alignItems: 'center' }}>
            <ScoreCircle value={impact} label="Impact" color="#2563eb" max="100" size={sCircle} fontSize={sFont} />
            <ScoreCircle value={methScore} label="Method" color="#7c3aed" max="70" size={sCircle} fontSize={sFont} />
            <ScoreCircle value={visScore} label="Visibility" color="#f59e0b" max="30" size={sCircle} fontSize={sFont} />
          </div>
        </div>

        {/* Main content grid */}
        <div style={{ display: 'grid', gridTemplateColumns: isSocial ? '1fr 1fr' : '1fr 1fr 1fr', gap: isSocial ? 8 : 12, flex: 1, overflow: 'hidden' }}>

          {/* Col 1: Takeaway + Research Insight */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: isSocial ? 6 : 10, overflow: 'hidden' }}>
            {takeaway && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: isSocial ? '6px 10px' : '10px 14px', borderLeft: '3px solid #2563eb', flex: insight ? '0 0 auto' : 1 }}>
                <div style={{ fontSize: isSocial ? 7 : 9, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>Key Takeaway</div>
                <div style={{ fontSize: bodyFs, lineHeight: 1.45, color: '#1e3a5f' }}>{tr(takeaway, maxTakeaway)}</div>
              </div>
            )}
            {insight && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: isSocial ? '6px 10px' : '10px 14px', borderLeft: '3px solid #16a34a' }}>
                <div style={{ fontSize: isSocial ? 7 : 9, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>Key Research Insight</div>
                <div style={{ fontSize: bodyFs, lineHeight: 1.45, color: '#14532d' }}>{tr(insight, maxInsight)}</div>
              </div>
            )}
            {overview && (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: isSocial ? '6px 10px' : '10px 14px', flex: 1 }}>
                <div style={{ fontSize: isSocial ? 7 : 9, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>Study Overview</div>
                <div style={{ fontSize: bodyFs, lineHeight: 1.45, color: '#334155' }}>{tr(overview, maxOverview)}</div>
              </div>
            )}
          </div>

          {/* Col 2: PICO + Study Details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: isSocial ? 6 : 10, overflow: 'hidden' }}>
            {(pico.population || pico.intervention || pico.comparator || pico.outcome) && (
              <Box title="PICO Framework" color="#2563eb">
                <PicoRow label="P" value={pico.population} color="#2563eb" fs={bodyFs} />
                <PicoRow label="I" value={pico.intervention} color="#16a34a" fs={bodyFs} />
                <PicoRow label="C" value={pico.comparator} color="#d97706" fs={bodyFs} />
                <PicoRow label="O" value={pico.outcome} color="#dc2626" fs={bodyFs} />
              </Box>
            )}
            <Box title="Study Details" style={{ flex: 1 }}>
              {[
                ['Sample Size', data.sample_size || '—'],
                ['Follow-up', data.follow_up_duration_months ? `${data.follow_up_duration_months} months` : '—'],
              ].map(([lbl, val]) => (
                <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', padding: '3px 0', fontSize: bodyFs }}>
                  <span style={{ color: '#94a3b8', fontWeight: 600 }}>{lbl}</span>
                  <span style={{ color: '#334155' }}>{val}</span>
                </div>
              ))}
              {evidence && (
                <div style={{ marginTop: 4, fontSize: bodyFs - 1, color: '#64748b' }}>
                  <span style={{ fontWeight: 600, color: '#475569' }}>Evidence: </span>{tr(evidence, 80)}
                </div>
              )}
            </Box>
          </div>

          {/* Col 3 (hidden on social → merged below): Strengths, Limitations, Gaps */}
          {!isSocial && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden' }}>
              {strengths.length > 0 && (
                <Box title="Strengths" color="#16a34a">
                  {strengths.slice(0, 4).map((s, i) => (
                    <div key={`ga-item-${i}`} style={{ fontSize: bodyFs, color: '#334155', lineHeight: 1.35, paddingLeft: 12, position: 'relative', marginBottom: 2 }}>
                      <span style={{ position: 'absolute', left: 0, color: '#16a34a', fontWeight: 700 }}>+</span>
                      {tr(s, maxStrength)}
                    </div>
                  ))}
                </Box>
              )}
              {limitations.length > 0 && (
                <Box title="Limitations" color="#dc2626">
                  {limitations.slice(0, 4).map((l, i) => (
                    <div key={`ga-item-${i}`} style={{ fontSize: bodyFs, color: '#334155', lineHeight: 1.35, paddingLeft: 12, position: 'relative', marginBottom: 2 }}>
                      <span style={{ position: 'absolute', left: 0, color: '#dc2626', fontWeight: 700 }}>-</span>
                      {tr(l, maxStrength)}
                    </div>
                  ))}
                </Box>
              )}
              {gaps.length > 0 && (
                <Box title="Research Gaps" color="#7c3aed" style={{ flex: 1 }}>
                  {gaps.slice(0, 3).map((g, i) => (
                    <div key={`ga-item-${i}`} style={{ fontSize: bodyFs, color: '#334155', lineHeight: 1.35, paddingLeft: 12, position: 'relative', marginBottom: 2 }}>
                      <span style={{ position: 'absolute', left: 0, color: '#7c3aed', fontWeight: 700 }}>?</span>
                      {tr(g, maxGap)}
                    </div>
                  ))}
                </Box>
              )}
            </div>
          )}
        </div>

        {/* Social-only: Strengths/Limitations/Gaps row at bottom */}
        {isSocial && (strengths.length > 0 || limitations.length > 0 || gaps.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
            {strengths.length > 0 && (
              <Box title="Strengths" color="#16a34a">
                {strengths.slice(0, 2).map((s, i) => (
                  <div key={`ga-item-${i}`} style={{ fontSize: 8, color: '#334155', lineHeight: 1.3, paddingLeft: 10, position: 'relative', marginBottom: 1 }}>
                    <span style={{ position: 'absolute', left: 0, color: '#16a34a', fontWeight: 700 }}>+</span>
                    {tr(s, maxStrength)}
                  </div>
                ))}
              </Box>
            )}
            {limitations.length > 0 && (
              <Box title="Limitations" color="#dc2626">
                {limitations.slice(0, 2).map((l, i) => (
                  <div key={`ga-item-${i}`} style={{ fontSize: 8, color: '#334155', lineHeight: 1.3, paddingLeft: 10, position: 'relative', marginBottom: 1 }}>
                    <span style={{ position: 'absolute', left: 0, color: '#dc2626', fontWeight: 700 }}>-</span>
                    {tr(l, maxStrength)}
                  </div>
                ))}
              </Box>
            )}
            {gaps.length > 0 && (
              <Box title="Research Gaps" color="#7c3aed">
                {gaps.slice(0, 2).map((g, i) => (
                  <div key={`ga-item-${i}`} style={{ fontSize: 8, color: '#334155', lineHeight: 1.3, paddingLeft: 10, position: 'relative', marginBottom: 1 }}>
                    <span style={{ position: 'absolute', left: 0, color: '#7c3aed', fontWeight: 700 }}>?</span>
                    {tr(g, maxGap)}
                  </div>
                ))}
              </Box>
            )}
          </div>
        )}
      </div>

      {/* ── FOOTER ── */}
      <div style={{
        marginTop: 'auto', flexShrink: 0,
        background: '#1e3a5f', padding: isSocial ? '5px 24px' : '8px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ color: '#93c5fd', fontSize: isSocial ? 8 : 10 }}>evidentica.net</span>
        <span style={{ color: '#64748b', fontSize: isSocial ? 7 : 9 }}>Generated by Evidentica Research Platform</span>
      </div>
    </div>
  );
};

/* ═══════════════════════ PAGE COMPONENT ══════════════════════════════ */
const GraphicalAbstractPage = () => {
  const { articleId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [ratio, setRatio] = useState('16:9');
  const canvasRef = useRef(null);
  const previewContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  useEffect(() => {
    const load = async () => {
      try { setData(await api.getGraphicalAbstractData(articleId)); }
      catch { toast.error('Failed to load article data'); }
      setLoading(false);
    };
    load();
  }, [articleId]);

  useEffect(() => {
    const updateWidth = () => {
      if (previewContainerRef.current) {
        setContainerWidth(previewContainerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [loading, ratio]);

  const getFilename = useCallback((ext) => {
    const slug = (data?.title || 'article').replace(/[^a-zA-Z0-9]+/g, '_').slice(0, 60);
    return `Evidentica_Abstract_${slug}.${ext}`;
  }, [data]);

  const downloadPng = async () => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const url = await toPng(canvasRef.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement('a');
      a.href = url; a.download = getFilename('png'); a.click();
      toast.success('PNG downloaded!');
    } catch (e) { toast.error('PNG export failed'); void 0; }
    setExporting(false);
  };

  const downloadSvg = async () => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const url = await toSvg(canvasRef.current, { cacheBust: true });
      const a = document.createElement('a');
      a.href = url; a.download = getFilename('svg'); a.click();
      toast.success('SVG downloaded!');
    } catch (e) { toast.error('SVG export failed'); void 0; }
    setExporting(false);
  };

  const downloadPdf = async () => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const f = FORMATS[ratio];
      const imgUrl = await toPng(canvasRef.current, { pixelRatio: 2, cacheBust: true });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [f.w, f.h] });
      pdf.addImage(imgUrl, 'PNG', 0, 0, f.w, f.h);
      pdf.save(getFilename('pdf'));
      toast.success('PDF downloaded!');
    } catch (e) { toast.error('PDF export failed'); void 0; }
    setExporting(false);
  };

  const fmt = FORMATS[ratio];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <PageSEO
        title={`Graphical Abstract${data ? ` — ${data.title}` : ''} | Evidentica`}
        description="Download a graphical abstract slide for your research presentation."
      />
      <nav className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center space-x-2">
              <FlaskConical className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold text-slate-900 dark:text-white">Evidentica</span>
            </Link>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Link to={`/article/${articleId}`}>
                <Button variant="ghost" size="sm" data-testid="back-to-article-btn">
                  <ArrowLeft className="mr-1 h-4 w-4" /> Back to Article
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3" data-testid="page-title">
              <Image className="h-6 w-6 sm:h-7 sm:w-7 text-indigo-600 flex-shrink-0" />
              Graphical Abstract
            </h1>
            {data && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-1">{data.title}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Select value={ratio} onValueChange={setRatio}>
              <SelectTrigger className="w-[140px] sm:w-[160px]" data-testid="ratio-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="16:9">16:9 (Widescreen)</SelectItem>
                <SelectItem value="4:3">4:3 (Standard)</SelectItem>
                <SelectItem value="social">Social Media (OG)</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={downloadPng} disabled={exporting || !data} data-testid="download-png-btn">
              {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} PNG
            </Button>
            <Button size="sm" variant="outline" onClick={downloadSvg} disabled={exporting || !data} data-testid="download-svg-btn">
              <Download className="mr-1.5 h-4 w-4" /> SVG
            </Button>
            <Button size="sm" onClick={downloadPdf} disabled={exporting || !data} data-testid="download-pdf-btn">
              <FileText className="mr-1.5 h-4 w-4" /> PDF
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
            <span className="ml-3 text-slate-500 dark:text-slate-400 text-lg">Loading abstract data...</span>
          </div>
        ) : data ? (
          <div ref={previewContainerRef} className="border dark:border-slate-700 rounded-xl overflow-hidden shadow-lg bg-white" data-testid="abstract-preview"
               style={{ width: '100%', maxWidth: fmt.w, position: 'relative', paddingBottom: `${(fmt.h / fmt.w) * 100}%` }}>
            <div style={{
              position: 'absolute', top: 0, left: 0,
              width: fmt.w, height: fmt.h,
              transform: `scale(${Math.min(1, containerWidth / fmt.w)})`,
              transformOrigin: 'top left',
            }}>
              <SlideCanvas data={data} ratio={ratio} canvasRef={canvasRef} />
            </div>
          </div>
        ) : (
          <div className="text-center py-20 text-slate-500">Article not found or no data available.</div>
        )}

        <p className="text-xs text-slate-400 dark:text-slate-600 mt-4 text-center">
          Download this graphical abstract to use in presentations, posters, or social media posts.
        </p>
      </div>
      <Footer />
    </div>
  );
};

export default GraphicalAbstractPage;
